import type { ApolloQueryResult} from '@apollo/client';
import { gql, useQuery } from '@apollo/client';
import { Exception } from '@livecontrol/core-ui';
import type { Event } from '@livecontrol/scheduler/model';
import type { DateTime } from 'luxon';
import { useEffect, useState } from 'react';
import type { QueryResult } from '../graphql';
import * as E_Dbx from './dbx';

interface TVariables {
  event: {
    extra_filters: {
      client_id: number;
      date_gte?: DateTime;
      date_lte?: DateTime;
      end_gte?: DateTime;
      end_lte?: DateTime;
      event_type?: string[];
      ids?: number[];
      limit?: number;
      location_ids?: string[];
      page?: number;
      wildcard?: string;
      ignore_pagination?: boolean;
      sort_field?: string;
      sort_order?: string;
      state?: string;
    };
    status?: string;
    location?: string;
    id?: number;
  };
}

interface TData {
  getEventsPaginated: {
    has_more: boolean;
    limit: number;
    page: number;
    result?: E_Dbx.Record[];
  };
}

export const QUERY = gql`
  query GetEventsPaginated($event: EventSearchInput!) {
    getEventsPaginated(event: $event) {
      has_more
      limit
      page
      success
      result {
        ...StandardEventResponse
      }
    }
  }
  ${E_Dbx.StandardEventResponse}
`;

type Args = TVariables;

interface Response {
  refetch?: (variables?: Partial<Args> | undefined) => Promise<ApolloQueryResult<TData>>;
}

export const useEvents = (args: Args): QueryResult<Event.Events, 'events'> & Response => {
  const [error, setError] = useState<Error | undefined>();
  const [events, setEvents] = useState<Event.Events | undefined>(undefined);

  // Prepare the query
  const { loading, data, refetch } = useQuery<TData, TVariables>(QUERY, {
    variables: args,
    fetchPolicy: 'cache-and-network',
    onError() {
      setError(Exception.KABOOM);
    }
  });

  // When available, parse server response
  useEffect(() => {
    let events_;
    let error_;

    if (data?.getEventsPaginated.result) {

      events_ = data.getEventsPaginated.result
        .map((record: E_Dbx.Record) => {
          const event = E_Dbx.normalize(record);

          return event;
        })
        .filter((event: Event | undefined): event is Event => !!event);

        const eventsResponse = {
          events: events_,
          page: data.getEventsPaginated.page,
          hasMore: data.getEventsPaginated.has_more
        };

        setEvents(eventsResponse);
        setError(error_);
    }

  }, [data]);

  return { loading, error, events, refetch };
};
